import React, { useContext, useState, useEffect } from "react";
import axios from "../api/axios";
import {
  Mail, Phone, LogOut, Copy, Check,
  ShieldCheck, TrendingUp, ShoppingBag,
  Hash, Tag, Star, CreditCard, FileText, Store, X
} from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import ProfileUpdateForm from "../components/ProfileUpdateForm";

/* ═══════════════════════════════════════════
   STYLES — NEUMORPHISM WITH VIBRANT COLORS
═══════════════════════════════════════════ */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .up-root {
    font-family: 'Montserrat', sans-serif;
    background: linear-gradient(135deg, #e0e5ec 0%, #d0d5dc 100%);
    min-height: 100%;
  }
  .up-root *, .up-root *::before, .up-root *::after { box-sizing: border-box; }
  .up-root ::-webkit-scrollbar { width: 4px; }
  .up-root ::-webkit-scrollbar-track { background: #e0e5ec; border-radius: 99px; }
  .up-root ::-webkit-scrollbar-thumb { background: #3b82f6; border-radius: 99px; }

  .up-page {
    max-width: 1280px; margin: 0 auto;
    padding: 6px 5px;
    display: flex; flex-direction: column; gap: 24px;
   
  }
  @media (min-width: 600px) {
  .up-page {
    margin-top: 100px;
  }
}
  @media (max-width: 600px) { .up-page { padding: 6px 5px; gap: 16px; } }

  /* ══════════════════════════════
     HERO BANNER - NEUMORPHISM
  ══════════════════════════════ */
  .up-hero {
    background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #2563eb 100%);
    border-radius: 9px;
    padding: 32px 36px;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.2), -8px -8px 16px rgba(255, 255, 255, 0.1);
  }
  .up-hero::before {
    content: '';
    position: absolute;
    top: -80px;
    right: -60px;
    width: 280px;
    height: 280px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(251,191,36,.2) 0%, transparent 70%);
    pointer-events: none;
  }
  .up-hero::after {
    content: '';
    position: absolute;
    bottom: -50px;
    left: 20%;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(59,130,246,.15) 0%, transparent 70%);
    pointer-events: none;
  }

  /* Avatar */
  .up-avatar-wrap { position: relative; z-index: 1; flex-shrink: 0; }
  .up-avatar-img {
    width: 100px;
    height: 100px;
    border-radius: 32px;
    object-fit: cover;
    border: 3px solid #fbbf24;
    background: linear-gradient(135deg, #1e3a8a, #2563eb);
    transition: all 0.3s ease;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.2), -4px -4px 8px rgba(255, 255, 255, 0.1);
  }
  .up-avatar-img:hover {
    transform: scale(1.05);
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.25), -6px -6px 12px rgba(255, 255, 255, 0.15);
  }
  .up-avatar {
    width: 100px;
    height: 100px;
    border-radius: 32px;
    background: linear-gradient(135deg, #1e3a8a, #2563eb);
    border: 3px solid #fbbf24;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 36px;
    font-weight: 900;
    color: #fbbf24;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.2), -4px -4px 8px rgba(255, 255, 255, 0.1);
  }
  @media (max-width: 520px) {
    .up-avatar, .up-avatar-img { width: 75px; height: 75px; font-size: 28px; border-radius: 24px; }
  }

  .up-hero-info { flex: 1; min-width: 0; position: relative; z-index: 1; }
  .up-hero-role {
    font-size: 11px; font-weight: 800; color: rgba(255, 255, 255, 0.8);
    text-transform: uppercase; letter-spacing: .1em; margin-bottom: 8px;
    display: inline-block;
    padding: 4px 12px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    backdrop-filter: blur(5px);
  }
  .up-hero-name {
    font-size: clamp(20px, 4vw, 28px); font-weight: 900; color: #fff;
    line-height: 1.2; margin-bottom: 8px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  }
  .up-hero-id {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 99px; padding: 5px 14px;
    font-size: 12px; font-weight: 700; color: #fcd34d;
    backdrop-filter: blur(5px);
  }

  .up-hero-actions { position: relative; z-index: 1; }
  .up-logout-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 12px 24px; border-radius: 30px; border: none; cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 13px; font-weight: 800;
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #fca5a5;
    transition: all .2s ease;
    white-space: nowrap;
    backdrop-filter: blur(5px);
  }
  .up-logout-btn:hover {
    background: rgba(239, 68, 68, 0.3);
    border-color: rgba(239, 68, 68, 0.6);
    transform: translateY(-2px);
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.2);
  }
  .up-logout-btn:active {
    transform: translateY(2px);
    box-shadow: inset 4px 4px 8px rgba(0, 0, 0, 0.2);
  }

  /* Floating orbs animation */
  .up-hero .floating-orb-1 {
    position: absolute;
    top: 15%;
    left: 5%;
    width: 60px;
    height: 60px;
    background: radial-gradient(circle, rgba(251,191,36,0.15) 0%, transparent 70%);
    border-radius: 50%;
    animation: float-reverse 8s ease-in-out infinite;
    pointer-events: none;
  }
  .up-hero .floating-orb-2 {
    position: absolute;
    bottom: 15%;
    right: 8%;
    width: 80px;
    height: 80px;
    background: radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%);
    border-radius: 50%;
    animation: float 10s ease-in-out infinite;
    pointer-events: none;
  }
  @keyframes float {
    0%, 100% { transform: translateY(0px) scale(1); }
    50% { transform: translateY(-20px) scale(1.1); }
  }
  @keyframes float-reverse {
    0%, 100% { transform: translateY(0px) scale(1); }
    50% { transform: translateY(15px) scale(0.9); }
  }

  /* ══════════════════════════════
     MAIN GRID
  ══════════════════════════════ */
  .up-grid {
    display: grid; grid-template-columns: 300px 1fr; gap: 24px; align-items: start;
  }
  @media (max-width: 900px) { .up-grid { grid-template-columns: 1fr; } }

  /* ══════════════════════════════
     SIDEBAR - NEUMORPHISM
  ══════════════════════════════ */
  .up-sidebar {
    background: #e8edf5;
    border-radius: 9px;
    padding: 24px;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.5);
    display: flex; flex-direction: column; gap: 24px;
  }

  .up-sidebar-title {
    font-size: 11px; font-weight: 800; color: #4b5563;
    text-transform: uppercase; letter-spacing: .09em;
    padding-bottom: 12px; margin-bottom: 4px;
    border-bottom: 2px solid rgba(59, 130, 246, 0.2);
  }

  /* Status items */
  .up-status-list { display: flex; flex-direction: column; gap: 12px; }
  .up-status-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 12px 16px; border-radius: 20px;
    background: #e8edf5;
    transition: all .15s;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .up-status-row:hover {
    transform: translateY(-2px);
    box-shadow: 6px 6px 12px rgba(0, 0, 0, 0.08), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .up-status-left { display: flex; align-items: center; gap: 10px; }
  .up-status-icon {
    width: 36px; height: 36px; border-radius: 18px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.08), -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .up-status-icon.kyc { background: #3b82f6; color: white; }
  .up-status-icon.share { background: #f59e0b; color: white; }
  .up-status-icon.product { background: #10b981; color: white; }
  .up-status-label { font-size: 13px; font-weight: 700; color: #1f2937; }
  .up-status-pill {
    padding: 4px 12px; border-radius: 99px;
    font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .05em;
  }
  .up-status-pill.active {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    box-shadow: 2px 2px 4px rgba(16, 185, 129, 0.3);
  }
  .up-status-pill.inactive {
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
    box-shadow: 2px 2px 4px rgba(239, 68, 68, 0.3);
  }

  /* Quick stats in sidebar */
  .up-sidebar-stats {
    display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  }
  .up-sidebar-stat {
    border-radius: 20px; padding: 14px;
    display: flex; flex-direction: column; gap: 4px;
    transition: all .2s;
  }
  .up-sidebar-stat.blue {
    background: linear-gradient(135deg, #dbeafe, #eff6ff);
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .up-sidebar-stat.amber {
    background: linear-gradient(135deg, #fef3c7, #fffbeb);
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .up-sidebar-stat.green {
    background: linear-gradient(135deg, #dcfce7, #f0fdf4);
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .up-sidebar-stat.purple {
    background: linear-gradient(135deg, #ede9fe, #f5f3ff);
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.05), inset -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .up-sidebar-stat-label {
    font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: .07em;
  }
  .up-sidebar-stat.blue .up-sidebar-stat-label { color: #1e40af; }
  .up-sidebar-stat.amber .up-sidebar-stat-label { color: #92400e; }
  .up-sidebar-stat.green .up-sidebar-stat-label { color: #065f46; }
  .up-sidebar-stat.purple .up-sidebar-stat-label { color: #5b21b6; }
  .up-sidebar-stat-val {
    font-size: 14px; font-weight: 900; color: #1f2937;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }

  /* ══════════════════════════════
     RIGHT CONTENT
  ══════════════════════════════ */
  .up-content { display: flex; flex-direction: column; gap: 20px; }

  /* Shared card - NEUMORPHISM */
  .up-card {
    background: #e8edf5;
    border-radius: 9px;
    padding: 24px;
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.1), -8px -8px 16px rgba(255, 255, 255, 0.5);
  }
  .up-card-title {
    font-size: 13px; font-weight: 800; color: #1f2937;
    text-transform: uppercase; letter-spacing: .07em; margin-bottom: 20px;
    display: flex; align-items: center; gap: 8px;
  }
  .up-card-title-bar {
    width: 4px; height: 20px; border-radius: 99px;
    background: linear-gradient(180deg, #3b82f6, #2563eb);
    flex-shrink: 0;
  }

  /* Contact cards */
  .up-contact-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
  }
  @media (max-width: 600px) { .up-contact-grid { grid-template-columns: 1fr; } }

  .up-contact-card {
    background: linear-gradient(135deg, #ffffff, #f3f6fc);
    border-radius: 9px;
    padding: 18px;
    transition: all .2s;
    cursor: default;
    box-shadow: 6px 6px 12px rgba(0, 0, 0, 0.08), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .up-contact-card:hover {
    transform: translateY(-3px);
    box-shadow: 12px 12px 24px rgba(0, 0, 0, 0.12), -8px -8px 16px rgba(255, 255, 255, 0.6);
  }
  .up-contact-top {
    display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;
  }
  .up-contact-label-wrap { display: flex; align-items: center; gap: 8px; }
  .up-contact-icon {
    width: 34px; height: 34px; border-radius: 17px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.08), -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .up-contact-icon.email { background: #3b82f6; color: white; }
  .up-contact-icon.phone { background: #10b981; color: white; }
  .up-contact-label { font-size: 11px; font-weight: 800; color: #6b7280; text-transform: uppercase; letter-spacing: .06em; }
  .up-copy-btn {
    width: 32px; height: 32px; border-radius: 16px; border: none;
    background: #e8edf5;
    cursor: pointer; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    color: #6b7280;
    transition: all .2s;
    box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.08), -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .up-copy-btn:hover {
    transform: translateY(-2px);
    box-shadow: 6px 6px 12px rgba(0, 0, 0, 0.12), -4px -4px 8px rgba(255, 255, 255, 0.6);
    color: #3b82f6;
  }
  .up-copy-btn:active {
    transform: translateY(2px);
    box-shadow: inset 3px 3px 6px rgba(0, 0, 0, 0.08), inset -3px -3px 6px rgba(255, 255, 255, 0.5);
  }
  .up-copy-btn.copied {
    background: #10b981;
    color: white;
  }
  .up-contact-val {
    font-size: 14px; font-weight: 700; color: #1f2937;
    word-break: break-all; line-height: 1.4;
  }
  .up-copied-text {
    font-size: 11px; font-weight: 700; color: #059669; margin-top: 6px;
    display: flex; align-items: center; gap: 4px;
  }

  /* Account details grid */
  .up-details-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px;
  }
  @media (max-width: 760px) { .up-details-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 420px) { .up-details-grid { grid-template-columns: 1fr; } }

  .up-detail-item {
    background: linear-gradient(135deg, #ffffff, #f3f6fc);
    border-radius: 9px;
    padding: 16px;
    transition: all .15s;
    box-shadow: 6px 6px 12px rgba(0, 0, 0, 0.06), -4px -4px 8px rgba(255, 255, 255, 0.5);
  }
  .up-detail-item:hover {
    transform: translateY(-2px);
    box-shadow: 10px 10px 20px rgba(0, 0, 0, 0.1), -6px -6px 12px rgba(255, 255, 255, 0.6);
  }
  .up-detail-icon-row {
    display: flex; align-items: center; gap: 8px; margin-bottom: 10px;
  }
  .up-detail-icon {
    width: 30px; height: 30px; border-radius: 15px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    box-shadow: 4px 4px 8px rgba(59, 130, 246, 0.3), -2px -2px 4px rgba(255, 255, 255, 0.5);
  }
  .up-detail-label {
    font-size: 10px; font-weight: 800; color: #6b7280;
    text-transform: uppercase; letter-spacing: .07em;
  }
  .up-detail-val {
    font-size: 13.5px; font-weight: 800; color: #1f2937;
    text-transform: capitalize;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .up-detail-val.muted { color: #9ca3af; font-weight: 600; }

  /* loading */
  .up-loading {
    min-height: 60vh; display: flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 600; color: #6b7280;
    font-family: 'Montserrat', sans-serif;
    background: #e8edf5;
    border-radius: 28px;
    margin: 24px;
    padding: 60px;
    box-shadow: inset 8px 8px 16px rgba(0, 0, 0, 0.05), inset -8px -8px 16px rgba(255, 255, 255, 0.5);
  }

  .up-security-card {
    margin-top: 12px;
    padding: 12px;
    border-radius: 16px;
    background: linear-gradient(135deg, #eff6ff, #f8fbff);
    border: 1px solid rgba(59, 130, 246, 0.16);
  }
  .up-security-btn {
    width: 100%; padding: 10px 12px; border: none; border-radius: 999px;
    background: linear-gradient(135deg, #2563eb, #3b82f6);
    color: #fff; font-size: 12px; font-weight: 800; cursor: pointer;
    transition: all .2s ease;
  }
  .up-security-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 20px rgba(37, 99, 235, 0.2);
  }

  .up-modal-overlay {
    position: fixed; inset: 0; z-index: 1000;
    background: rgba(15, 23, 42, 0.7); backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center; padding: 20px;
  }
  .up-modal-card {
    width: min(100%, 480px); border-radius: 20px; padding: 24px;
    background: #f8fbff; box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
    border: 1px solid rgba(255, 255, 255, 0.8);
  }
  .up-modal-header {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 16px;
  }
  .up-modal-title {
    font-size: 18px; font-weight: 900; color: #1f2937;
  }
  .up-modal-subtitle {
    margin-top: 4px; font-size: 13px; color: #6b7280;
  }
  .up-modal-close {
    width: 34px; height: 34px; border: none; border-radius: 999px; background: #e8edf5;
    color: #4b5563; cursor: pointer; font-size: 20px; line-height: 1;
  }
  .up-form-stack { display: flex; flex-direction: column; gap: 12px; }
  .up-form-field label {
    display: block; font-size: 12px; font-weight: 800; color: #4b5563; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.06em;
  }
  .up-input {
    width: 100%; border: 1px solid #dbe4f0; border-radius: 12px; padding: 11px 12px;
    font-size: 14px; color: #111827; background: #fff; transition: all .2s ease;
  }
  .up-input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15); }
  .up-modal-actions { display: flex; gap: 10px; margin-top: 16px; }
  .up-btn-primary, .up-btn-secondary {
    flex: 1; padding: 11px 14px; border-radius: 12px; border: none; font-size: 13px; font-weight: 800; cursor: pointer;
  }
  .up-btn-primary {
    background: linear-gradient(135deg, #2563eb, #3b82f6); color: #fff;
  }
  .up-btn-secondary {
    background: #e8edf5; color: #374151;
  }
  .up-alert {
    padding: 10px 12px; border-radius: 12px; font-size: 13px; font-weight: 600; margin-bottom: 12px;
  }
  .up-alert-error { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
  .up-alert-success { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }

  /* ══════════════════════════════
     SKELETON
  ══════════════════════════════ */
  .up-skel {
    border-radius: 28px;
    background: linear-gradient(135deg, #e8edf5, #dce3ec);
    position: relative;
    overflow: hidden;
    box-shadow: inset 3px 3px 6px rgba(0, 0, 0, 0.05), inset -3px -3px 6px rgba(255, 255, 255, 0.5);
  }
  .up-skel::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 25%, rgba(59, 130, 246, 0.1) 50%, transparent 75%);
    background-size: 200% 100%;
    animation: up-shimmer 1.4s infinite;
  }
  @keyframes up-shimmer {
    from { background-position: -200% 0; }
    to { background-position: 200% 0; }
  }

  /* Responsive Breakpoints */
  @media (max-width: 768px) {
    .up-hero {
      padding: 20px 24px;
      gap: 16px;
    }
    .up-avatar-img, .up-avatar {
      width: 70px;
      height: 70px;
    }
    .up-avatar {
      font-size: 28px;
    }
    .up-hero-name {
      font-size: 18px;
    }
    .up-logout-btn {
      padding: 8px 18px;
      font-size: 12px;
    }
  }

  @media (max-width: 640px) {
    .up-hero {
      flex-wrap: wrap;
      padding: 20px;
      gap: 16px;
    }
    .up-avatar-wrap {
      width: 100%;
      display: flex;
      justify-content: center;
    }
    .up-hero-info {
      text-align: center;
      width: 100%;
    }
    .up-hero-id {
      justify-content: center;
    }
    .up-hero-actions {
      width: 100%;
      display: flex;
      justify-content: center;
    }
    .up-logout-btn {
      width: 100%;
      justify-content: center;
    }
  }

  @media (max-width: 480px) {
    .up-hero {
      flex-direction: column;
      text-align: center;
      padding: 20px;
    }
    .up-avatar-img, .up-avatar {
      width: 90px;
      height: 90px;
    }
    .up-avatar {
      font-size: 32px;
    }
    .up-hero-info {
      text-align: center;
    }
    .up-hero-id {
      justify-content: center;
    }
  }
`;

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const UserProfilePage = () => {
  const { user, logout } = useContext(AuthContext);
  const [copied, setCopied] = useState("");
  const [detailedUser, setDetailedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchaseStatus, setPurchaseStatus] = useState({ hasSharePurchase: false, hasProductPurchase: false });
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [fpStep, setFpStep] = useState(1);
  const [fpMobile, setFpMobile] = useState(user?.mobile || "");
  const [fpName, setFpName] = useState(user?.name || "");
  const [fpUniqueId, setFpUniqueId] = useState("");
  const [fpSponsorId, setFpSponsorId] = useState("");
  const [fpUserId, setFpUserId] = useState("");
  const [fpNewPassword, setFpNewPassword] = useState("");
  const [fpConfirmPassword, setFpConfirmPassword] = useState("");
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState("");
  const [fpSuccess, setFpSuccess] = useState("");

  // Mobile Store application state
  const [mobileStoreApp, setMobileStoreApp] = useState(null);
  const [showMobileStoreModal, setShowMobileStoreModal] = useState(false);
  const [shopName, setShopName] = useState("");
  const [shopAddress, setShopAddress] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [applying, setApplying] = useState(false);
  const [appError, setAppError] = useState("");
  const [appSuccess, setAppSuccess] = useState("");

  useEffect(() => {
    const fetchMobileStoreApplication = async () => {
      try {
        const res = await axios.get("/auth/mobile-store-application");
        setMobileStoreApp(res.data.application);
      } catch (error) {
        // No application yet or request failed; leave as null
      }
    };

    fetchMobileStoreApplication();
  }, []);

  const handleApplyMobileStore = async () => {
    if (!shopName || !shopAddress) {
      setAppError("Shop name and address are required");
      return;
    }

    setApplying(true);
    setAppError("");
    setAppSuccess("");

    try {
      await axios.post("/auth/apply-mobile-store", { shopName, shopAddress, gstNumber });

      setAppSuccess("Mobile Store application submitted successfully!");
      setShowMobileStoreModal(false);
      setShopName("");
      setShopAddress("");
      setGstNumber("");

      const res = await axios.get("/auth/mobile-store-application");
      setMobileStoreApp(res.data.application);

      setTimeout(() => setAppSuccess(""), 3000);
    } catch (err) {
      setAppError(err.response?.data?.message || "Failed to submit application");
    } finally {
      setApplying(false);
    }
  };

  useEffect(() => {
    const fetchDetailedUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/member`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setDetailedUser(res.data.user);
        setPurchaseStatus({
          hasSharePurchase: !!res.data.user?.hasSharePurchase,
          hasProductPurchase: !!res.data.user?.hasProductPurchase
        });
      } catch (error) {
        console.error("Error fetching detailed user data:", error);
        setDetailedUser(user);
      } finally {
        setLoading(false);
      }
    };

    fetchDetailedUserData();
  }, [user]);

  const copyToClipboard = (text, field) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(""), 2000);
  };

  const resetForgotPasswordModal = () => {
    setShowForgotModal(false);
    setFpStep(1);
    setFpMobile(user?.mobile || "");
    setFpName(user?.name || "");
    setFpUniqueId("");
    setFpSponsorId("");
    setFpUserId("");
    setFpNewPassword("");
    setFpConfirmPassword("");
    setFpError("");
    setFpSuccess("");
  };

  const handleForgotVerify = async (e) => {
    e.preventDefault();
    setFpError("");
    setFpSuccess("");

    if (!fpMobile || !fpName || !fpUniqueId || !fpSponsorId) {
      setFpError("Please enter all required fields");
      return;
    }

    setFpLoading(true);
    try {
      const res = await axios.post("/auth/forgot-password/verify", {
        mobile: fpMobile,
        name: fpName,
        uniqueId: fpUniqueId,
        sponsorId: fpSponsorId,
      });
      setFpUserId(res.data.userId);
      setFpStep(2);
    } catch (err) {
      setFpError(err.response?.data?.message || "User not found");
    } finally {
      setFpLoading(false);
    }
  };

  const handleForgotReset = async (e) => {
    e.preventDefault();
    setFpError("");
    setFpSuccess("");

    if (!fpNewPassword || !fpConfirmPassword) {
      setFpError("Please enter and confirm new password");
      return;
    }

    if (fpNewPassword !== fpConfirmPassword) {
      setFpError("Passwords do not match");
      return;
    }

    setFpLoading(true);
    try {
      await axios.post("/auth/forgot-password/reset", {
        userId: fpUserId,
        newPassword: fpNewPassword,
      });
      setFpSuccess("Password updated successfully! You can now sign in again.");
      setTimeout(() => {
        resetForgotPasswordModal();
      }, 1800);
    } catch (err) {
      setFpError(err.response?.data?.message || "Failed to reset password");
    } finally {
      setFpLoading(false);
    }
  };

  if (loading || !user) return (
    <div className="up-root">
      <style>{styles}</style>
      <div className="up-loading">{loading ? "Loading profile…" : "No user data found"}</div>
    </div>
  );

  return (
    <div className="up-root">
      <style>{styles}</style>
      <div className="up-page">

        {/* Hero */}
        <div className="up-hero">
          <div className="floating-orb-1"></div>
          <div className="floating-orb-2"></div>
          <div className="up-avatar-wrap">
            {detailedUser?.profilePic ? (
              <img
                src={detailedUser.profilePic}
                alt={detailedUser.name || "Profile"}
                className="up-avatar-img"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://ui-avatars.com/api/?background=3b82f6&color=fff&bold=true&name=" + (user?.name || "User");
                }}
              />
            ) : (
              <div className="up-avatar">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
            )}
          </div>
          <div className="up-hero-info">
            {/* <div className="up-hero-role">{user?.role || "Member"}</div> */}
            <div className="up-hero-name">{user?.name}</div>
            <div className="up-hero-id">
              <Hash size={14} /> {user?.uniqueId || "N/A"}
            </div>
            <div className="up-hero-id">
              <Phone size={14} /> {user?.mobile || "N/A"}
            </div>
          </div>
          <div className="up-hero-actions">
            <button className="up-logout-btn" onClick={logout}>
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>

        {/* Grid */}
        <div className="up-grid">

          {/* Sidebar */}
          <div className="up-sidebar">

            {/* Status */}
            <div>
              <div className="up-sidebar-title">Account Status</div>
              <div className="up-status-list">
                <StatusRow
                  iconClass="kyc" icon={<ShieldCheck size={16} />}
                  label="KYC" active={user?.kycStatus === "verified"}
                />
                <StatusRow
                  iconClass="share" icon={<TrendingUp size={16} />}
                  label="Loan (LBBP)" active={purchaseStatus.hasSharePurchase}
                />
                <StatusRow
                  iconClass="product" icon={<ShoppingBag size={16} />}
                  label="Product (PU&AP)" active={purchaseStatus.hasProductPurchase}
                />
              </div>
            </div>

            {/* Quick info tiles */}
            <div>
              <div className="up-sidebar-title" style={{ marginBottom: 10 }}>Sponsor Details</div>
              <div className="up-sidebar-stats">
                <StatTile variant="amber" label="Sponsor ID" val={detailedUser?.sponsorDetails?.uniqueId || "—"} />
                <StatTile variant="green" label="Sponsor Name" val={detailedUser?.sponsorDetails?.name || "—"} />
                <StatTile variant="purple" label="Sponsor Mobile" val={detailedUser?.sponsorDetails?.mobile || "—"} />
                <StatTile
                  variant="blue"
                  label="Registration Date"
                  val={
                    detailedUser?.createdAt
                      ? new Date(detailedUser.createdAt).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                      : "—"
                  }
                />
              </div>
              {/* password reset option */}
              <div>
                <div className="up-sidebar-title" style={{ marginBottom: 10, marginTop: 20 }}>Security</div>
                <div className="up-security-card">
                  <button type="button" className="up-security-btn" onClick={() => setShowForgotModal(true)}>
                    Change Password
                  </button>
                </div>
              </div>

              {/* Mobile Store Application */}
              {user?.userType !== "mobile_store" && (
                <div>
                  <div className="up-sidebar-title" style={{ marginBottom: 10, marginTop: 20 }}>Mobile Store</div>
                  <div
                    className="up-security-card"
                    style={{
                      background:
                        mobileStoreApp?.status === "approved"
                          ? "linear-gradient(135deg, #f0fdf4, #dcfce7)"
                          : mobileStoreApp?.status === "rejected"
                          ? "linear-gradient(135deg, #fef2f2, #fee2e2)"
                          : mobileStoreApp?.status === "pending"
                          ? "linear-gradient(135deg, #fffbeb, #fef3c7)"
                          : "linear-gradient(135deg, #eff6ff, #f8fbff)",
                      border:
                        mobileStoreApp?.status === "approved"
                          ? "1px solid #bbf7d0"
                          : mobileStoreApp?.status === "rejected"
                          ? "1px solid #fecaca"
                          : mobileStoreApp?.status === "pending"
                          ? "1px solid #fde68a"
                          : "1px solid rgba(59, 130, 246, 0.16)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                      <Store size={16} color="#2563eb" />
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: "#374151" }}>
                        {mobileStoreApp?.status === "approved"
                          ? `Approved — Store ID: ${mobileStoreApp?.mobileStoreId}`
                          : mobileStoreApp?.status === "rejected"
                          ? `Rejected: ${mobileStoreApp?.rejectionReason || "No reason provided"}`
                          : mobileStoreApp?.status === "pending"
                          ? "Application pending admin review"
                          : "Apply to become a Mobile Store"}
                      </span>
                    </div>
                    {!mobileStoreApp && (
                      <button type="button" className="up-security-btn" onClick={() => setShowMobileStoreModal(true)}>
                        Apply Now
                      </button>
                    )}
                    {appSuccess && (
                      <div className="up-alert up-alert-success" style={{ marginTop: 10, marginBottom: 0 }}>
                        {appSuccess}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* Right Content */}
          <div className="up-content">

            <ProfileUpdateForm />

            {/* Contact */}
            <div className="up-card">
              <div className="up-card-title">
                <div className="up-card-title-bar" /> Contact Information
              </div>
              <div className="up-contact-grid">
                <ContactCard
                  variant="email" icon={<Mail size={14} />}
                  label="Email Address" value={user?.email}
                  onCopy={() => copyToClipboard(user?.email, "email")}
                  isCopied={copied === "email"}
                />
                <ContactCard
                  variant="phone" icon={<Phone size={14} />}
                  label="Phone Number" value={user?.mobile}
                  onCopy={() => copyToClipboard(user?.mobile, "mobile")}
                  isCopied={copied === "mobile"}
                />
              </div>
            </div>

            {/* Account Details */}
            <div className="up-card">
              <div className="up-card-title">
                <div className="up-card-title-bar" /> Account Information
              </div>
              <div className="up-details-grid">
                <DetailItem icon={<Tag size={12} />} label="Unique ID" value={user?.uniqueId} />
                <DetailItem icon={<Star size={12} />} label="Role" value={user?.role} />
              </div>
            </div>

            {/* KYC Information */}
            <div className="up-card">
              <div className="up-card-title">
                <div className="up-card-title-bar" /> KYC Information
              </div>
              <div className="up-details-grid">
                <DetailItem icon={<ShieldCheck size={12} />} label="KYC Status" value={(detailedUser || user)?.kycStatus || "Not Submitted"} />
                <DetailItem icon={<FileText size={12} />} label="PAN Number" value={(detailedUser || user)?.kycDocuments?.pan || "N/A"} />
                <DetailItem icon={<FileText size={12} />} label="Aadhaar Number" value={(detailedUser || user)?.kycDocuments?.aadhaar || "N/A"} />
                <DetailItem icon={<CreditCard size={12} />} label="Bank Name" value={(detailedUser || user)?.kycDocuments?.bankName || "N/A"} />
                <DetailItem icon={<CreditCard size={12} />} label="Account Number" value={(detailedUser || user)?.kycDocuments?.bankAccount || "N/A"} />
                <DetailItem icon={<CreditCard size={12} />} label="IFSC Code" value={(detailedUser || user)?.kycDocuments?.ifsc || "N/A"} />
              </div>
            </div>

          </div>
        </div>

        {showForgotModal && (
          <div className="up-modal-overlay" onClick={resetForgotPasswordModal}>
            <div className="up-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="up-modal-header">
                <div>
                  <div className="up-modal-title">Reset Password</div>
                  <div className="up-modal-subtitle">Verify your details to create a new password.</div>
                </div>
                <button type="button" className="up-modal-close" onClick={resetForgotPasswordModal} aria-label="Close reset password modal">
                  ×
                </button>
              </div>

              {fpError && <div className="up-alert up-alert-error">{fpError}</div>}
              {fpSuccess && <div className="up-alert up-alert-success">{fpSuccess}</div>}

              {fpStep === 1 ? (
                <form onSubmit={handleForgotVerify} className="up-form-stack">
                  <div className="up-form-field">
                    <label>Registered Mobile Number</label>
                    <input
                      type="text"
                      className="up-input"
                      value={fpMobile}
                      onChange={(e) => setFpMobile(e.target.value)}
                      placeholder="Enter your mobile number"
                      required
                    />
                  </div>
                  <div className="up-form-field">
                    <label>Registered Name</label>
                    <input
                      type="text"
                      className="up-input"
                      value={fpName}
                      onChange={(e) => setFpName(e.target.value)}
                      placeholder="Enter your registered name"
                      required
                    />
                  </div>
                  <div className="up-form-field">
                    <label>Unique ID</label>
                    <input
                      type="text"
                      className="up-input"
                      value={fpUniqueId}
                      onChange={(e) => setFpUniqueId(e.target.value)}
                      placeholder="Enter your unique ID"
                      required
                    />
                  </div>
                  <div className="up-form-field">
                    <label>Sponsor ID</label>
                    <input
                      type="text"
                      className="up-input"
                      value={fpSponsorId}
                      onChange={(e) => setFpSponsorId(e.target.value)}
                      placeholder="Enter sponsor ID"
                      required
                    />
                  </div>
                  <div className="up-modal-actions">
                    <button type="button" className="up-btn-secondary" onClick={resetForgotPasswordModal}>Cancel</button>
                    <button type="submit" className="up-btn-primary" disabled={fpLoading}>
                      {fpLoading ? "Verifying..." : "Verify"}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleForgotReset} className="up-form-stack">
                  <div className="up-form-field">
                    <label>New Password</label>
                    <input
                      type="password"
                      className="up-input"
                      value={fpNewPassword}
                      onChange={(e) => setFpNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      required
                    />
                  </div>
                  <div className="up-form-field">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      className="up-input"
                      value={fpConfirmPassword}
                      onChange={(e) => setFpConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      required
                    />
                  </div>
                  <div className="up-modal-actions">
                    <button type="button" className="up-btn-secondary" onClick={() => setFpStep(1)}>
                      Back
                    </button>
                    <button type="submit" className="up-btn-primary" disabled={fpLoading}>
                      {fpLoading ? "Updating..." : "Set Password"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        {showMobileStoreModal && (
          <div
            className="up-modal-overlay"
            onClick={() => {
              setShowMobileStoreModal(false);
              setAppError("");
            }}
          >
            <div className="up-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="up-modal-header">
                <div>
                  <div className="up-modal-title">Apply for Mobile Store</div>
                  <div className="up-modal-subtitle">Provide your shop details to submit an application.</div>
                </div>
                <button
                  type="button"
                  className="up-modal-close"
                  onClick={() => {
                    setShowMobileStoreModal(false);
                    setAppError("");
                  }}
                  aria-label="Close mobile store application modal"
                >
                  <X size={18} />
                </button>
              </div>

              {appError && <div className="up-alert up-alert-error">{appError}</div>}

              <div className="up-form-stack">
                <div className="up-form-field">
                  <label>Shop Name *</label>
                  <input
                    type="text"
                    className="up-input"
                    value={shopName}
                    onChange={(e) => setShopName(e.target.value)}
                    placeholder="Enter your shop name"
                  />
                </div>
                <div className="up-form-field">
                  <label>Shop Address *</label>
                  <input
                    type="text"
                    className="up-input"
                    value={shopAddress}
                    onChange={(e) => setShopAddress(e.target.value)}
                    placeholder="Enter your shop address"
                  />
                </div>
                <div className="up-form-field">
                  <label>GST Number (Optional)</label>
                  <input
                    type="text"
                    className="up-input"
                    value={gstNumber}
                    onChange={(e) => setGstNumber(e.target.value)}
                    placeholder="Enter GST number if applicable"
                  />
                </div>
                <div className="up-modal-actions">
                  <button
                    type="button"
                    className="up-btn-secondary"
                    onClick={() => {
                      setShowMobileStoreModal(false);
                      setAppError("");
                    }}
                  >
                    Cancel
                  </button>
                  <button type="button" className="up-btn-primary" onClick={handleApplyMobileStore} disabled={applying}>
                    {applying ? "Submitting..." : "Submit Application"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* Sub-components */
const StatusRow = ({ iconClass, icon, label, active }) => (
  <div className="up-status-row">
    <div className="up-status-left">
      <div className={`up-status-icon ${iconClass}`}>{icon}</div>
      <span className="up-status-label">{label}</span>
    </div>
    <span className={`up-status-pill ${active ? "active" : "inactive"}`}>
      {active ? "Active" : "Inactive"}
    </span>
  </div>
);

const StatTile = ({ variant, label, val }) => (
  <div className={`up-sidebar-stat ${variant}`}>
    <div className="up-sidebar-stat-label">{label}</div>
    <div className="up-sidebar-stat-val">{val}</div>
  </div>
);

const ContactCard = ({ variant, icon, label, value, onCopy, isCopied }) => (
  <div className="up-contact-card">
    <div className="up-contact-top">
      <div className="up-contact-label-wrap">
        <div className={`up-contact-icon ${variant}`}>{icon}</div>
        <span className="up-contact-label">{label}</span>
      </div>
      <button className={`up-copy-btn ${isCopied ? "copied" : ""}`} onClick={onCopy}>
        {isCopied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
    <div className="up-contact-val">{value || "N/A"}</div>
    {isCopied && (
      <div className="up-copied-text"><Check size={12} /> Copied!</div>
    )}
  </div>
);

const DetailItem = ({ icon, label, value, fallback = "N/A", muted = false }) => {
  const display = value || fallback;
  return (
    <div className="up-detail-item">
      <div className="up-detail-icon-row">
        <div className="up-detail-icon">{icon}</div>
        <span className="up-detail-label">{label}</span>
      </div>
      <div className={`up-detail-val ${muted ? "muted" : ""}`}>{display}</div>
    </div>
  );
};

export default UserProfilePage;